import { Box, IconButton, TextField } from '@material-ui/core';
import { Autocomplete, AutocompleteRenderInputParams } from '@material-ui/lab';
import {
	ChangeEvent,
	FormEvent,
	ReactElement,
	useEffect,
	useState
} from 'react';
import { connect } from 'react-redux';
import { BehaviorSubject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import SearchIcon from '@material-ui/icons/Search';

import {
	AppState,
	AppThunkDispatch,
	BosatlasActions,
	bosatlasGetSuggestions
} from '../../../redux';

import './bosatlas-search-field.styles.scss';
import { useBrandName } from '../../../hooks';
import { useTranslation } from 'react-i18next';
import { config } from '../../../config';

export interface IBosatlasSearchFieldStateProps {
	suggestions?: string[];
	getSuggestions: (searchValue: string) => void;
}

export interface IBosatlasSearchFieldOwnProps {
	onSearch: (searchValue: string) => void;
}

function BosatlasSearchFieldView(
	props: IBosatlasSearchFieldStateProps & IBosatlasSearchFieldOwnProps
): ReactElement {
	const { suggestions, getSuggestions, onSearch } = props;
	const brandName: string = useBrandName();
	const { t } = useTranslation('myBosatlas');

	const { minCharactersForSearch } = config;
	const searchDelay: number = 500;

	const [searchSubscription, setSearchSubscription] =
		useState<Subscription>();
	const [searchValue, setSearchValue] = useState<string>('');

	const [searchSubject] = useState<BehaviorSubject<string>>(
		new BehaviorSubject<string>(searchValue)
	);

	const [isInputFocused, setIsInputFocused] = useState<boolean>(false);

	useEffect(() => {
		setSearchSubscription(
			searchSubject
				.pipe(debounceTime(searchDelay))
				.subscribe((value: string) => {
					if (value.length >= minCharactersForSearch) {
						getSuggestions(value);
					}
				})
		);
		return cleanUp;
	}, []);

	useEffect(() => {
		searchSubject.next(searchValue);
	}, [searchValue]);

	function handleChange(event: ChangeEvent<HTMLInputElement>): void {
		setSearchValue(event.target.value);
	}

	function cleanUp(): void {
		if (searchSubscription) {
			searchSubscription.unsubscribe();
		}
	}

	function handleSubmit(event: FormEvent): void {
		event.preventDefault();
		onSearch(searchValue);
	}

	function onAutocompleteChange(_: ChangeEvent<{}>, value: string): void {
		setSearchValue(value);
		onSearch(value);
	}

	return (
		<Box className="bosatlas-search-field">
			<Autocomplete
				className="bosatlas-search-field-input"
				fullWidth
				disableClearable
				value={searchValue}
				onChange={onAutocompleteChange}
				renderInput={(params: AutocompleteRenderInputParams) => (
					<form onSubmit={handleSubmit}>
						<TextField
							{...params}
							placeholder={t('search.searchFieldPlaceholder', {
								brandName
							})}
							variant="outlined"
							onChange={handleChange}
							onFocus={() => setIsInputFocused(true)}
							onBlur={() => setIsInputFocused(false)}
							InputProps={{
								endAdornment: (
									<IconButton
										color={
											isInputFocused
												? 'primary'
												: undefined
										}
										onClick={() => onSearch(searchValue)}
									>
										<SearchIcon />
									</IconButton>
								)
							}}
						/>
					</form>
				)}
				options={suggestions ? suggestions : []}
				freeSolo
			/>
		</Box>
	);
}

function mapStateToProps(state: AppState) {
	return {
		suggestions: state.bosatlas.searchSuggestions
	};
}

function mapDispatchToProps(dispatch: AppThunkDispatch<BosatlasActions>) {
	return {
		getSuggestions: (searchValue: string) =>
			dispatch(bosatlasGetSuggestions(searchValue))
	};
}

export const BosatlasSearchField = connect(
	mapStateToProps,
	mapDispatchToProps
)(BosatlasSearchFieldView);
